/**
 * Graphique de trajectoire Décret Tertiaire (OPERAT).
 * Affiche les objectifs de réduction -40 % 2030 / -50 % 2040 / -60 % 2050
 * par rapport à l'année de référence, avec la trajectoire réelle du site.
 */
'use client';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../lib/cn';

/* ──────────────────────── Types ──────────────────────────────────── */
export interface TrajectoryDataPoint {
  year: number;
  reference?: number;   // Consommation de référence (kWh/m²)
  target?: number;      // Objectif réglementaire
  actual?: number;      // Consommation réelle (si disponible)
  forecast?: number;    // Prévision (si programme de travaux planifié)
}

interface TrajectoryChartProps {
  /** Consommation de référence en kWh ep/m²/an */
  referenceConsumption?: number;
  /** Surface assujettie en m² */
  surface?: number;
  /** Données personnalisées (écrasent le calcul auto) */
  data?: TrajectoryDataPoint[];
  className?: string;
}

/* ──────────────────────── Calcul cible OPERAT ────────────────────── */
// Cibles réglementaires Décret Tertiaire :
//   2030 : -40 % (ratio 0.60), 2040 : -50 % (0.50), 2050 : -60 % (0.40)
// Ces ratios sont appliqués via interpolation dans buildDefaultData ci-dessous.

function buildDefaultData(reference: number): TrajectoryDataPoint[] {
  const years = [2020, 2023, 2026, 2030, 2035, 2040, 2045, 2050];
  return years.map((year) => {
    const targetRatio =
      year <= 2030
        ? 1 - ((year - 2020) / 10) * 0.4
        : year <= 2040
          ? 0.6 - ((year - 2030) / 10) * 0.1
          : 0.5 - ((year - 2040) / 10) * 0.1;
    return {
      year,
      target: Math.round(reference * targetRatio),
      reference,
    };
  });
}

/* ──────────────────────── Tooltip custom ─────────────────────────── */
interface TooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string | number;
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-stone-200 bg-white px-3 py-2 shadow-medium text-xs">
      <p className="font-semibold text-ink mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name === 'target' ? 'Objectif OPERAT' : entry.name === 'actual' ? 'Réel' : 'Prévision'} :{' '}
          <span className="font-semibold">{entry.value} kWh ep/m²</span>
        </p>
      ))}
    </div>
  );
}

/* ──────────────────────── Composant principal ────────────────────── */
export function TrajectoryChart({
  referenceConsumption = 250,
  data: customData,
  className,
}: TrajectoryChartProps) {
  const data = customData ?? buildDefaultData(referenceConsumption);

  const targets = [
    { year: 2030, label: '-40 %', value: Math.round(referenceConsumption * 0.6) },
    { year: 2040, label: '-50 %', value: Math.round(referenceConsumption * 0.5) },
    { year: 2050, label: '-60 %', value: Math.round(referenceConsumption * 0.4) },
  ];

  return (
    <div className={cn('space-y-4', className)}>
      {/* Chips objectifs */}
      <div className="flex flex-wrap gap-2">
        {targets.map((t) => (
          <div
            key={t.year}
            className="flex items-center gap-2 rounded-lg border border-lavender-200 bg-lavender-50/60 px-3 py-1.5 text-sm"
          >
            <span className="h-2 w-2 rounded-full bg-lavender-500" />
            <span className="font-medium text-lavender-900">{t.year}</span>
            <span className="text-lavender-700">{t.label} →</span>
            <span className="font-semibold text-lavender-900">{t.value} kWh ep/m²</span>
          </div>
        ))}
      </div>

      {/* Graphique */}
      <div className="h-64 w-full rounded-2xl border border-stone-200 bg-white p-4 shadow-subtle">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="gradTarget" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: '#78716c' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#78716c' }}
              axisLine={false}
              tickLine={false}
              unit=" kWh"
              width={70}
            />
            <Tooltip content={<ChartTooltip />} />
            <ReferenceLine
              y={referenceConsumption}
              stroke="#d6d3d1"
              strokeDasharray="4 4"
              label={{ value: 'Réf.', position: 'insideTopRight', fontSize: 10, fill: '#a8a29e' }}
            />
            <Area
              type="monotone"
              dataKey="target"
              name="target"
              stroke="#8b5cf6"
              strokeWidth={2}
              fill="url(#gradTarget)"
              dot={false}
              activeDot={{ r: 4, fill: '#8b5cf6' }}
            />
            {data.some((d) => d.actual !== undefined) && (
              <Area
                type="monotone"
                dataKey="actual"
                name="actual"
                stroke="#22c55e"
                strokeWidth={2}
                strokeDasharray="5 3"
                fill="url(#gradActual)"
                dot={false}
                activeDot={{ r: 4, fill: '#22c55e' }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-stone-400">
        Source&nbsp;: Décret n°&thinsp;2019-771 du 23 juillet 2019 — Dispositif Éco-Énergie Tertiaire.
        Consommation exprimée en kWh d&apos;énergie primaire / m² / an, corrigée des variations
        climatiques.
      </p>
    </div>
  );
}
